import { useState, useEffect } from 'react'
import { TextToggle } from '../ui-components/TextToggle'
import NameValueTableRow, { GROUPED_STATIC_TEXT_ROLE } from '../ui-components/NameValueTableRow'
import { useMetaDataContext } from '../../state-management/contexts/MetaDataContext'
import { convertNumberToCurrency } from '../../utils/utils'
import TextAndInfo from '../ui-components/TextAndInfo'
import VaulDrawer from '../BottomSheetComponents/VaulDrawer'
import { FundHoldingsBottomSheet } from '../BottomSheetComponents/FundHoldingsContent'
import { useMutualFundDetailScrollEvent } from '../../hooks/useMutualFundDetailScrollEvent'
import { AUMChange, MutualFundDetailSections, MutualFundDetailSubSections } from '../../types/mutual-fund-detail.types'
import { sortTenures } from '../../utils/mutualFundDetail'
import { renderSubSection } from '../../utils/sectionsConfig'

export default function FundHoldings({
  sectionKey,
  subsections
}: {
  sectionKey: `${MutualFundDetailSections}`
  subsections: `${MutualFundDetailSubSections}`[]
}) {
  const { data } = useMetaDataContext()
  const [openBottomSheet, setOpenBottomSheet] = useState(false)
  const [aumChange, setAumChange] = useState<
    {
      text: string
      tenure: any
      value: number
    }[]
  >([])
  const [toggleOptions, setToggleOptions] = useState<string[]>([])
  const [AUMChangeToggle, setAUMChangeToggle] = useState('')
  const [currentAUMChangeValue, setCurrentAUMChangeValue] = useState(0)

  const sectionRef = useMutualFundDetailScrollEvent({ sectionKey })

  const handleToggleClick = (toggleOption: string) => {
    setAUMChangeToggle(() => toggleOption)
    setCurrentAUMChangeValue(() => aumChange?.filter((datum) => datum?.text === toggleOption)?.[0]?.value)
  }

  useEffect(() => {
    const aumChange = data?.aumChange ?? []
    const sortedTenures = sortTenures(aumChange?.map((datum) => datum?.tenure))
    let sortedAumChange: AUMChange[] = []
    sortedTenures?.forEach((datum) => {
      const found = aumChange?.find(
        (changeDatum) => changeDatum?.tenure?.period === datum?.period && changeDatum?.tenure?.value === datum?.value
      )
      if (found) sortedAumChange?.push(found)
    })
    const toggleOptionsCopy = sortedAumChange?.map((datum) => {
      return {
        ...datum,
        text: `${datum?.tenure?.value}${datum?.tenure?.period === 'MONTH' ? 'M' : 'Y'}`
      }
    })
    const toggleOptionsTexts = toggleOptionsCopy?.map((datum) => datum?.text) ?? []
    setToggleOptions(() => toggleOptionsTexts)
    setAUMChangeToggle(() => toggleOptionsTexts?.[0] ?? '')
    setAumChange(() => toggleOptionsCopy)
    setCurrentAUMChangeValue(() => toggleOptionsCopy[0]?.value)
  }, [data])

  return (
    <div ref={sectionRef} className='bg-white rounded-[12px] flex flex-col w-full overflow-hidden'>
      <div className='flex flex-col w-full gap-3 px-5 pt-5 pb-5'>
        <div className='pb-1'>
          <TextAndInfo text='Fund Holdings' onInfoClick={() => setOpenBottomSheet((open) => !open)} />
        </div>

        <NameValueTableRow
          name='AUM'
          value={convertNumberToCurrency({ amount: data?.aum ? +data?.aum : 0, isRound: true })}
          groupForAccessibility
        />

        <AumChangeRow
          toggleOptions={toggleOptions}
          currentToggleOption={AUMChangeToggle}
          onToggleClick={handleToggleClick}
          changeValue={currentAUMChangeValue}
        />

        {/* <NameValueTableRow
          name='Liquidity Ratio'
          value={
            convertUnitAndValue({
              value: data?.liquidityRatio?.amount ?? 0,
              unit: data?.liquidityRatio?.unit ?? `${UNITS.PERCENTAGE}`
            }) ?? ''
          }
        /> */}
      </div>
      {subsections?.map((subsection, idx) => {
        return <div key={`${subsection}${idx}`}> {renderSubSection({ componentKey: subsection })} </div>
      })}
      <VaulDrawer open={openBottomSheet} setOpen={setOpenBottomSheet}>
        <FundHoldingsBottomSheet
          onDismiss={() => setOpenBottomSheet((open) => !open)}
          ScreenName={`MF_L2_Fund_Holdings`}
        />
        {/* <FundInformationContent onDismiss={() => setOpenBottomSheet((open) => !open)} /> */}
      </VaulDrawer>
    </div>
  )
}

export function AumChangeRow({
  toggleOptions,
  currentToggleOption,
  onToggleClick,
  changeValue
}: {
  toggleOptions: string[]
  currentToggleOption: string
  onToggleClick: (toggleOption: string) => void
  changeValue: number
}) {
  let textColor = 'black'
  let sign = ''
  if (changeValue > 0) {
    textColor = '#0B6B2A'
    sign = '+'
  } else if (changeValue < 0) {
    textColor = '#E77464'
    sign = '-'
  }

  const formattedAmount = convertNumberToCurrency({ amount: Math.abs(changeValue) })
  const hasToggleOptions = toggleOptions?.length > 0

  return (
    <div className='flex justify-between min-h-[24px] items-center gap-2'>
      <div className='text-[#6A6E6E] text-[14px] font-[400] flex gap-[4px] items-center flex-wrap'>
        <div
          role={GROUPED_STATIC_TEXT_ROLE}
          aria-label={`${hasToggleOptions ? `AUM change in ${currentToggleOption}` : 'AUM change'}, ${sign ? `${sign} ${formattedAmount}` : formattedAmount}`}
        >
          <span aria-hidden='true'>AUM change {hasToggleOptions ? 'in' : ''}</span>
        </div>
        <TextToggle
          currentToggleOption={currentToggleOption}
          toggleOptions={toggleOptions}
          onToggleClick={onToggleClick}
        />
      </div>
      <div aria-hidden='true' style={{ color: textColor }} className='text-[14px] font-[500]'>
        {sign} {formattedAmount}
      </div>
    </div>
  )
}
